import { useEffect, useRef } from 'react';
import { useOrgParams } from '~/common/use-org-params';
import { useMap, useMapEvents } from 'react-leaflet';
import { latLngBounds } from 'leaflet';
import { usePlotPolygons } from './use-plot-polygons';
import 'leaflet/dist/leaflet.css';

const MAP_ZOOM_PADDING = [5, 5]
const MAP_ZOOM_DURATION_S = 0.18 // Snappy, quick transition (~180ms)
const MAP_ZOOM_EASE_LINEARITY = 0.35 // More responsive curve, but still smooth

/**
 * Component that handles zoom animation to active plot
 * Must be inside MapContainer to access map instance via useMap()
 */
export const MapZoomController = ({
  farmId,
  fullImage,
  imageBounds,
  onMapClick,
  disableAutoZoom = false,
  animRef,
  orgZoom = true,
}) => {
  const map = useMap()
  const { plotId } = useOrgParams()
  const { plots } = usePlotPolygons(farmId)
  // Prevents the plotId effect from restarting an animation already started imperatively via animRef
  const imperativeFired = useRef(false)
  // Holds the currently-registered moveend handler so it can be cancelled on rapid clicks
  const pendingCompleteRef = useRef(null)

  // Register imperative flyTo trigger so MapCard can start animation before React state updates.
  // Accepts an optional onComplete callback that fires when the animation ends (via moveend event).
  useEffect(() => {
    if (!animRef) return
    animRef.current = (targetPlotId, onComplete) => {
      if (!map) { onComplete?.(); return }

      // Cancel any pending moveend handler from a previous click (rapid-click safety)
      if (pendingCompleteRef.current) {
        map.off('moveend', pendingCompleteRef.current)
        pendingCompleteRef.current = null
      }

      const container = map.getContainer()

      const scheduleComplete = () => {
        if (orgZoom) container.classList.add('tp-map-zooming')
        const handler = () => {
          pendingCompleteRef.current = null
          if (orgZoom) container.classList.remove('tp-map-zooming')
          onComplete?.()
        }
        pendingCompleteRef.current = handler
        map.once('moveend', handler)
      }

      if (targetPlotId) {
        // Plot zoom respects disableAutoZoom (prevents unwanted zoom on initial render in fullscreen)
        if (disableAutoZoom) { onComplete?.(); return }
        const plot = plots?.find(p => p.id === targetPlotId)
        if (plot?.points?.length >= 3) {
          imperativeFired.current = true
          map.flyToBounds(latLngBounds(plot.points.map(p => [p.y, p.x])), {
            padding: MAP_ZOOM_PADDING,
            duration: MAP_ZOOM_DURATION_S,
            easeLinearity: MAP_ZOOM_EASE_LINEARITY
          })
          scheduleComplete()
        } else {
          onComplete?.()
        }
      } else {
        // Farm-bounds zoom — always allowed (user-initiated), matches the reactive deselect zoom
        if (imageBounds) {
          imperativeFired.current = true
          map.flyToBounds(imageBounds, {
            padding: [0, 0],
            duration: MAP_ZOOM_DURATION_S,
            easeLinearity: MAP_ZOOM_EASE_LINEARITY
          })
          scheduleComplete()
        } else {
          onComplete?.()
        }
      }
    }
  }, [animRef, map, plots, disableAutoZoom, imageBounds, orgZoom])

  // Handle background clicks and polygon visibility during zoom animations
  useMapEvents({
    click: () => {
      // Only trigger if clicking directly on the map (not on a polygon)
      // Polygon clicks will stopPropagation, so this won't fire for them
      onMapClick?.()
    },
  })

  // Re-fit view when container resizes (triggered by map.invalidateSize() in MapResizer)
  useEffect(() => {
    if (!map) return

    const onResize = () => {
      // Skip if the container has no valid size (e.g. display:none at a responsive breakpoint)
      const size = map.getSize()
      if (!size.x || !size.y) return

      if (fullImage && imageBounds) {
        map.fitBounds(imageBounds, { padding: [0, 0], animate: false })
        return
      }
      if (disableAutoZoom) return

      if (plotId && plots?.length > 0) {
        const activePlot = plots.find(p => p.id === plotId)
        if (activePlot?.points?.length >= 3) {
          const positions = activePlot.points.map(p => [p.y, p.x])
          map.fitBounds(latLngBounds(positions), { padding: MAP_ZOOM_PADDING, animate: false })
          return
        }
      }

      const allPositions = plots?.flatMap(plot => plot.points?.map(p => [p.y, p.x]) || []) || []
      if (allPositions.length > 0) {
        map.fitBounds(latLngBounds(allPositions), { padding: MAP_ZOOM_PADDING, animate: false })
      }
    }

    map.on('resize', onResize)
    return () => { map.off('resize', onResize) }
  }, [map, fullImage, imageBounds, disableAutoZoom, plotId, plots])

  // Animate to plot bounds when plotId changes
  useEffect(() => {
    if (!map) return

    // Skip if the container has no valid size (e.g. display:none at a responsive breakpoint)
    const size = map.getSize()
    if (!size.x || !size.y) return

    // Skip if animation was already started imperatively — avoids restarting an in-progress flyTo
    if (imperativeFired.current) {
      imperativeFired.current = false
      return
    }

    // Always fit full image to viewport (even in disableAutoZoom mode — needed for initial render)
    if (fullImage && imageBounds) {
      if (disableAutoZoom) {
        map.fitBounds(imageBounds, { padding: [0, 0], animate: false })
      } else {
        // flyToBounds gives a smooth animated transition instead of a snap
        map.flyToBounds(imageBounds, {
          padding: [0, 0],
          duration: MAP_ZOOM_DURATION_S,
          easeLinearity: MAP_ZOOM_EASE_LINEARITY
        })
      }
      return
    }

    if (disableAutoZoom) return

    if (!plots || plots.length === 0) return

    if (plotId) {
      // Find the active plot
      const activePlot = plots.find(plot => plot.id === plotId)

      if (activePlot && activePlot.points && activePlot.points.length >= 3) {
        // Convert {x, y} points to [[y, x], ...] Leaflet format
        const positions = activePlot.points.map(p => [p.y, p.x])

        // Calculate bounds from positions
        const bounds = latLngBounds(positions)
        
        // Animate to bounds with padding
        map.flyToBounds(bounds, {
          padding: MAP_ZOOM_PADDING,
          duration: MAP_ZOOM_DURATION_S,
          easeLinearity: MAP_ZOOM_EASE_LINEARITY
        })
      }
    } else {
      // No plotId - zoom back to show all plots (farm view)
      if (plots.length > 0) {
        // Calculate bounds from all plots
        const allPositions = plots.flatMap(plot =>
          plot.points?.map(p => [p.y, p.x]) || []
        )

        if (allPositions.length > 0) {
          const bounds = latLngBounds(allPositions)
          map.flyToBounds(bounds, { padding: MAP_ZOOM_PADDING, duration: MAP_ZOOM_DURATION_S, easeLinearity: MAP_ZOOM_EASE_LINEARITY })
        }
      }
    }
  }, [disableAutoZoom, fullImage, imageBounds, map, plotId, plots])

  return null
}
